import "../css/subpage.less";
import React, { useEffect, useMemo, useState } from "react";
import LocalizedLink from "../components/LocalizedLink";
import { getImageUrl } from "../config";
import { useTranslation } from "react-i18next";
import { fetchLocalizedJson } from "../api/fetchLocalizedJson";

import PageMeta from "../common/PageMeta";
import SkeletonView from "../common/SkeletonView";
import SubpageTitle from "../common/SubpageTitle";
import * as JobEntity from "../entities/Jobs";
import FaqStickyLink from "../common/FaqStickyLink";
import { PAGE_META } from "../seo/pageMeta";

function recruitImageForTitle(title: string): string {
  if (title.includes("エンジニア") || title.includes("Engineer")) {
    return "/image/img_recruit_engineer.png";
  }
  if (title.includes("プロジェクト") || title.includes("Project")) {
    return "/image/img_recruit_pj.png";
  }
  if (title.includes("営業") || title.includes("Sales")) {
    return "/image/img_recruit_sales.png";
  }
  return "/image/img_recruit_mv.png";
}

function altForRecruitTitle(title: string): string {
  return title;
}

/** 表示順: PM → エンジニア → 営業 */
function sortJobsForDisplay(jobs: JobEntity.Job[]): JobEntity.Job[] {
  const keys = ["プロジェクト", "Project", "エンジニア", "Engineer", "営業", "Sales"] as const;
  const rank = (t: string) => {
    const i = keys.findIndex((k) => t.includes(k));
    return i === -1 ? keys.length : Math.floor(i / 2);
  };
  return [...jobs].sort((a, b) => rank(a.title) - rank(b.title));
}

function recruitSectionId(title: string): string {
  if (title.includes("エンジニア") || title.includes("Engineer")) {
    return "recruit-engineer";
  }
  if (title.includes("プロジェクト") || title.includes("Project")) {
    return "recruit-pm";
  }
  if (title.includes("営業") || title.includes("Sales")) {
    return "recruit-sales";
  }
  return "recruit-section";
}

function displayRecruitTitle(title: string, t: (key: string) => string): string {
  if (title.includes("プロジェクト") || title.includes("Project")) {
    return `${t("recruit_page.pm_main")}${t("recruit_page.pm_sub")}`;
  }
  if (title.includes("エンジニア") || title.includes("Engineer")) {
    return `${t("recruit_page.eng_main")}${t("recruit_page.eng_sub")}`;
  }
  if (title.includes("営業") || title.includes("Sales")) {
    return `${t("recruit_page.sales_main")}${t("recruit_page.sales_sub")}`;
  }
  return title;
}

function getRoleLine(item: JobEntity.Job, t: (key: string) => string): string | null {
  const duty = item.contents.find((c) => c.title === "職務内容" || c.title === "Job Duty" || c.title === "职务内容");
  if (!duty?.description) {
    return null;
  }
  return `${t("recruit_page.role_prefix")}${duty.description}`;
}

function getJobDutyBullets(item: JobEntity.Job): string[] {
  const duty = item.contents.find((c) => c.title === "職務内容" || c.title === "Job Duty" || c.title === "职务内容");
  return duty?.list ?? [];
}

type QualificationBlock = {
  label: string;
  items: string[];
};

function getQualificationBlocks(item: JobEntity.Job): QualificationBlock[] {
  const titles = ["必須要件", "歓迎要件", "求める人物像", "Requirements", "Preferred Skills", "Desired Candidate", "必备条件", "加分条件", "理想人才画像"];
  const res: QualificationBlock[] = [];
  for (const c of item.contents) {
    const title = c.title;
    if (title && titles.some(t => title.includes(t)) && c.list && c.list.length > 0) {
      res.push({
        label: title,
        items: c.list,
      });
    }
  }

  return res;
}


function parseSalary(desc: string | undefined): { main: string; note: string } {
  if (!desc) {
    return { main: "", note: "" };
  }
  const lines = desc
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return { main: "", note: "" };
  }
  if (lines.length === 1) {
    return { main: lines[0], note: "" };
  }
  return { main: lines[0], note: lines.slice(1).join("\n") };
}

function getApplySectionData(jobs: JobEntity.Job[]): {
  applyMethod: JobEntity.Content | null;
  selection: JobEntity.Content | null;
  contactRaw: string | null;
} {
  let applyMethod: JobEntity.Content | null = null;
  let selection: JobEntity.Content | null = null;
  let contactRaw: string | null = null;

  for (const job of jobs) {
    for (const c of job.contents) {
      if (!applyMethod && (c.title === "応募方法" || c.title === "Application Method" || c.title === "应聘方式")) {
        applyMethod = c;
      }
      if (!selection && (c.title === "選考プロセス" || c.title === "Selection Process" || c.title === "选考流程")) {
        selection = c;
      }
      if (!contactRaw && (c.title === "電話番号" || c.title === "Phone Number" || c.title === "电话号码")) {
        contactRaw = c.description ?? null;
      }

    }
  }
  return { applyMethod, selection, contactRaw };
}

function parseRecruitContact(raw: string | null): {
  tel: string;
  attribution: string;
} {
  if (!raw?.trim()) {
    return { tel: "", attribution: "" };
  }
  const m = raw.trim().match(/^([\d-]+)\s*（(.+)）\s*$/);
  if (m) {
    return { tel: m[1], attribution: `（${m[2]}）` };
  }
  return { tel: raw.trim(), attribution: "" };
}

const RecruitApplySection: React.FC<{
  applyMethod: JobEntity.Content | null;
  selection: JobEntity.Content | null;
  contactRaw: string | null;
}> = ({ applyMethod, selection, contactRaw }) => {
  const { t } = useTranslation();
  const { tel, attribution } = parseRecruitContact(contactRaw);
  if (!applyMethod && !selection) {
    return null;
  }

  return (
    <section
      id="recruit-apply"
      className="subpage-section recruit-apply"
      aria-label={t("recruit_page.apply_title")}
    >
      <header className="recruit-apply__header">
        <div className="recruit-apply__header-row">
          <span className="recruit-apply__bar" aria-hidden />
          <h2 className="recruit-apply__title">{t("recruit_page.apply_title")}</h2>
        </div>
        <div className="recruit-apply__header-rule" aria-hidden />
      </header>

      <div className="recruit-apply__panel">
        <div className="recruit-apply__grid">
          {applyMethod ? (
            <div className="recruit-apply__col">
              <div className="recruit-apply__col-head">{t("recruit_page.apply_method")}</div>
              <div className="recruit-apply__col-body">
                {applyMethod.description ? (
                  <p className="recruit-apply__intro">{applyMethod.description}</p>
                ) : null}
                {applyMethod.list?.length ? (
                  <ul className="recruit-apply__list">
                    {applyMethod.list.map((line, i) => (
                      <li key={`am-${i}`}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          ) : null}

          {selection ? (
            <div className="recruit-apply__col">
              <div className="recruit-apply__col-head">{t("recruit_page.selection_process")}</div>
              <div className="recruit-apply__col-body">
                {selection.list?.length ? (
                  <ul className="recruit-apply__list">
                    {selection.list.map((line, i) => (
                      <li key={`sel-${i}`}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {tel ? (
          <div className="recruit-apply__contact">
            <p className="recruit-apply__contact-lead">
              {t("recruit_page.phone_inquiry_lead")}
            </p>
            <p className="recruit-apply__tel">
              TEL.{tel}
            </p>
            {attribution ? (
              <p className="recruit-apply__contact-name">{attribution}</p>
            ) : null}
            <LocalizedLink to="/inquiry" className="recruit-apply__mail-btn">
              {t("recruit_page.email_btn")}
            </LocalizedLink>
          </div>
        ) : (
          <div className="recruit-apply__contact">
            <LocalizedLink to="/inquiry" className="recruit-apply__mail-btn">
              {t("recruit_page.email_btn")}
            </LocalizedLink>
          </div>
        )}
      </div>
    </section>
  );
};

const RecruitJobArticle: React.FC<{
  item: JobEntity.Job;
  index: number;
}> = ({ item, index }) => {
  const { t } = useTranslation();
  const isSales = item.title.includes("営業") || item.title.includes("Sales") || item.title.includes("销售");
  const roleLine = getRoleLine(item, t);
  const dutyBullets = getJobDutyBullets(item);
  const qualBlocks = getQualificationBlocks(item);
  const salaryBlock = item.contents.find((c) => c.title && (c.title.includes("給与") || c.title.includes("Salary") || c.title.includes("薪酬")));

  const { main: salaryMain, note: salaryNote } = parseSalary(
    salaryBlock?.description,
  );
  const benefitsList = salaryBlock?.list ?? [];

  const dutyBlock = (
    <>
      <h3 className="recruit-detail__section-title">● {t("recruit_page.job_contents")}</h3>
      <ul className="recruit-detail__bullets">
        {dutyBullets.map((line, li) => (
          <li key={`duty-${li}`}>{line}</li>
        ))}
      </ul>
    </>
  );

  return (
    <article
      id={recruitSectionId(item.title)}
      className="recruit-detail"
    >
      <header className="recruit-detail__header">
        <div className="recruit-detail__header-row">
          <span className="recruit-detail__bar" aria-hidden />
          <h2 className="recruit-detail__title">
            {displayRecruitTitle(item.title, t)}
          </h2>
        </div>
        <div className="recruit-detail__header-rule" aria-hidden />
      </header>

      {isSales ? (
        <>
          <div className="recruit-detail__split recruit-detail__split--sales-top">
            <div className="recruit-detail__main">
              {item.description ? (
                <p className="recruit-detail__lead">{item.description}</p>
              ) : null}
            </div>
            <div className="recruit-detail__media">
              <img
                src={getImageUrl(recruitImageForTitle(item.title))}
                alt={altForRecruitTitle(item.title)}
              />
            </div>
          </div>
          <div className="recruit-detail__after-split">
            {roleLine ? (
              <p className="recruit-detail__role-line">{roleLine}</p>
            ) : null}
            {dutyBlock}
          </div>
        </>
      ) : (
        <div className="recruit-detail__split">
          <div className="recruit-detail__main">
            {roleLine ? (
              <p className="recruit-detail__role-line">{roleLine}</p>
            ) : null}
            {dutyBlock}
          </div>
          <div className="recruit-detail__media">
            <img
              src={getImageUrl(recruitImageForTitle(item.title))}
              alt={altForRecruitTitle(item.title)}
            />
          </div>
        </div>
      )}

      {qualBlocks.length > 0 ? (
        <section
          className="recruit-detail__qual"
          aria-labelledby={`recruit-qual-${index}`}
        >
          <h3
            id={`recruit-qual-${index}`}
            className="recruit-detail__section-title"
          >
            ● {t("recruit_page.qualifications")}
          </h3>
          {qualBlocks.map((qb, qi) => (
            <div key={`${qb.label}-${qi}`} className="recruit-detail__qual-group">
              <span className="recruit-detail__badge">{qb.label}</span>
              <ul className="recruit-detail__bullets">
                {qb.items.map((line, li) => (
                  <li key={`q-${qi}-${li}`}>{line}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ) : null}

      <section
        className="recruit-detail__salary"
        aria-labelledby={`recruit-salary-${index}`}
      >
        <h3
          id={`recruit-salary-${index}`}
          className="recruit-detail__section-title"
        >
          ● {t("recruit_page.salary_title")}
        </h3>
        {salaryMain ? (
          <p className="recruit-detail__salary-main">{salaryMain}</p>
        ) : null}
        {salaryNote ? (
          <p className="recruit-detail__salary-note">{salaryNote}</p>
        ) : null}
      </section>

      <div
        className={
          benefitsList.length > 0
            ? "recruit-detail__footer"
            : "recruit-detail__footer recruit-detail__footer--cta-only"
        }
      >
        {benefitsList.length > 0 ? (
          <div className="recruit-detail__notes">
            <div className="recruit-detail__notes-head">{t("recruit_page.notes")}</div>
            <ul className="recruit-detail__bullets recruit-detail__bullets--notes">
              {benefitsList.map((line, li) => (
                <li key={`note-${li}`}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="recruit-detail__cta">
          <a href="#recruit-apply" className="recruit-detail__cta-btn">
            {t("recruit_page.apply_title")}
          </a>
        </div>
      </div>
    </article>
  );
};

const Recruit: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [items, setEvents] = useState<JobEntity.Job[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchLocalizedJson<JobEntity.Job[]>("recruit.json")
      .then((data) => {
        setEvents(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [i18n.language]);

  const recruitMvNav = [
    {
      hash: "#recruit-pm",
      main: t("recruit_page.pm_main"),
      sub: t("recruit_page.pm_sub"),
    },
    {
      hash: "#recruit-engineer",
      main: t("recruit_page.eng_main"),
      sub: t("recruit_page.eng_sub"),
    },
    {
      hash: "#recruit-sales",
      main: t("recruit_page.sales_main"),
      sub: t("recruit_page.sales_sub"),
    },
  ];

  const orderedJobs = useMemo(() => sortJobsForDisplay(items), [items]);
  const applySection = useMemo(
    () => getApplySectionData(orderedJobs),
    [orderedJobs],
  );

  if (loading) {
    return <SkeletonView />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="subpage subpage--recruit">
      <PageMeta path="/recruit" {...PAGE_META["/recruit"]} />
      <SubpageTitle titleJa="採用情報" titleEn="Recruit" as="h1" />

      <FaqStickyLink />
      <section className="subpage-section recruit-mv" aria-label="採用メインビジュアル">
        <div className="recruit-mv__hero">
          <div className="recruit-mv__copy">
            <p className="recruit-mv__line">{t("recruit_page.catchphrase_1")}</p>
            <p className="recruit-mv__line">{t("recruit_page.catchphrase_2")}</p>
          </div>
          <div className="recruit-mv__visual">
            <img
              className="recruit-mv__img"
              src={getImageUrl("/image/img_recruit_mv.png")}
              alt=""
            />
          </div>
        </div>
        <nav className="recruit-mv__nav" aria-label="募集職種へのページ内リンク">
          {recruitMvNav.map((item) => (
            <a key={item.hash} href={item.hash} className="recruit-mv__nav-item">
              <span className="recruit-mv__nav-main">{item.main}</span>
              <span className="recruit-mv__nav-sub">{item.sub}</span>
              <span className="recruit-mv__chevron" aria-hidden />
            </a>
          ))}
        </nav>
      </section>
      <section className="subpage-section recruit-roles" aria-label="募集職種">
        {orderedJobs.map((item, index) => (
          <RecruitJobArticle key={item.title} item={item} index={index} />
        ))}
      </section>
      <RecruitApplySection
        applyMethod={applySection.applyMethod}
        selection={applySection.selection}
        contactRaw={applySection.contactRaw}
      />
    </div>
  );
};

export default Recruit;
